import React, { CSSProperties, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import { useGridMode } from 'contexts/GridModeContext';
import { useIsTableOnScreen } from 'contexts/TableOnScreenContext';
import gsap from 'gsap';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { useTableScopedOpen } from 'hooks/useTableScopedOpen';
import {
  SAFE_AREA_LEFT,
  SAFE_AREA_RIGHT,
  safeAreaMaxHeight,
  safeAreaModalBackdropPadding
} from 'utils/safeArea';
import { isWithinTapSlop, swallowNextClickNear, tapOrigin, TapOrigin } from 'utils/tapGesture';
import { swipeIgnoreProps } from 'utils/swipeIgnore';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';
import {
  getGridVisibility,
  GridVisibility,
  subscribeGridVisibility
} from 'components/MultiTableGrid/gridScrollChannel';

type GameModalProps = {
  open: boolean;
  onClose?: () => void;
  title?: string;
  children: React.ReactNode;
  /** Show close X button (default true) */
  showClose?: boolean;
  /**
   * With `ariaLabelledBy`, Escape calls `onClose`. Defaults to `showClose`, so
   * Escape follows the X; set it for a dialog without the X whose own Cancel
   * button closes it.
   */
  closeOnEscape?: boolean;
  /** Max width for larger screens */
  maxWidth?: string;
  /** When true, modal is contained within its parent (for grid mode) instead of viewport-fixed */
  inline?: boolean;
  /**
   * When true (the DEFAULT), a tap/click on the backdrop outside the card
   * dismisses the modal — the behaviour players expect of every other app.
   *
   * This used to default to FALSE because a slider drag released past the card
   * edge fires a `click` whose target IS the backdrop, slamming the modal shut
   * mid-adjust. Switching the whole behaviour off is no longer how we guard
   * against that: a dismiss now requires the pointer to go DOWN *and* UP on the
   * backdrop itself, so a gesture that merely ENDS out there is ignored. See
   * handleScrimPointerDown / handleScrimPointerUp below.
   *
   * Opt OUT only where an accidental dismiss is destructive, or where the player
   * must make an explicit choice (identity checks, chip purchases, prompts whose
   * dismissal would strand them on a dead table). Keep that list short.
   */
  closeOnBackdropClick?: boolean;
  /**
   * Lock the modal to a PORTRAIT layout on mobile-landscape. This modal portals
   * to <body>, outside the lobby's ForcePortrait wrapper, so a modal opened from
   * the portrait-locked lobby (e.g. TournamentDetailModal) would otherwise keep
   * the device's landscape orientation and show sideways relative to the lobby.
   * Opt in for lobby-launched modals; leave off for in-table modals (the table
   * view isn't portrait-locked). Ignored for inline/grid mode.
   */
  forcePortrait?: boolean;
  /**
   * Horizontal alignment of the header title. Defaults to 'left', which is how
   * every existing consumer reads — this is opt-in per modal, not a theme-wide
   * change. 'center' is for short, single-message dialogs whose body and action
   * are already centred (SeatJoinErrorModal), where a left title looked like it
   * belonged to a different card. Pair it with `showClose={false}`: the close X
   * is an absolutely-positioned corner button, so a long centred title would
   * run underneath it.
   */
  titleAlign?: 'left' | 'center';
  /**
   * Opt in to dialog semantics: the card becomes `role="dialog"`, named by the
   * element with this id (a title the consumer renders in its body). It takes
   * focus on open once the player can see it: its table is on screen and, in
   * MultiTableGrid, its cell is at least partly inside the grid's scrolled row
   * (and not while the player is typing in another table), returns it on close,
   * closes on Escape like the X, and outside a grid is `aria-modal` with Tab
   * kept inside the card. Unset, the card is exactly as before. EN-11109's
   * Sit & Go result modal uses it.
   */
  ariaLabelledBy?: string;
};

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * The MultiTableGrid cell an element sits in, by the key the grid publishes its
 * visibility under (data-grid-cell). Null outside that grid, including the
 * carousel's tiled panels, which are gridMode too.
 */
const gridCellIdOf = (el: Element | null): string | null =>
  el?.closest('[data-grid-cell]')?.getAttribute('data-grid-cell') ?? null;

/** Whether any of this MultiTableGrid cell is inside the grid's scrolled row. */
const isCellInGridRow = (fractions: GridVisibility, gridCellId: string): boolean =>
  (fractions[gridCellId] ?? 0) > 0;

/**
 * Whether focus handed back to `target` would land where the player can see
 * it: not in a parked carousel panel, and, inside MultiTableGrid, in a cell of
 * the scrolled row while the grid itself is on screen. `gridHidden` is set when
 * the card's own grid cell is not on screen, which means the whole grid is
 * behind the single-table view.
 */
const isFocusTargetSeen = (target: HTMLElement, gridHidden: boolean): boolean => {
  if (target.closest('[data-table-panel-held]')) return false;
  const targetCellId = gridCellIdOf(target);
  if (targetCellId === null) return true;
  return !gridHidden && isCellInGridRow(getGridVisibility(), targetCellId);
};

const isEditableField = (el: Element | null): boolean =>
  el instanceof HTMLElement &&
  (el.isContentEditable ||
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT');

/**
 * Branded modal wrapper matching the Hijack dark theme.
 * Responsive: full-width sheet on mobile, centered card on desktop.
 * Features an animated border glow that traces around the card edge.
 */
const GameModal = ({
  open,
  onClose,
  title,
  children,
  showClose = true,
  closeOnEscape = showClose,
  maxWidth = '420px',
  inline = false,
  closeOnBackdropClick = true,
  forcePortrait = false,
  titleAlign = 'left',
  ariaLabelledBy
}: GameModalProps) => {
  const isGridMode = useGridMode();
  const isInline = inline || isGridMode;
  // A modal belongs to the table it was opened on. In single-table mode this
  // one portals to <body>, escaping the parked panel's `visibility: hidden`,
  // and several in-table modals read app-wide GlobalContext flags — so without
  // scoping, a buy-in / add-chips opened on one table carried over onto the
  // next one the player swiped to. See hooks/useTableScopedOpen.
  const isOpen = useTableScopedOpen(open);
  const { reduceAnimations } = useAnimationPreferences();
  const reduceAnimationsRef = useRef(reduceAnimations);
  reduceAnimationsRef.current = reduceAnimations;
  const borderTweenRef = useRef<gsap.core.Tween | null>(null);
  // Mirror ForcePortrait's own lock condition (see ForcePortrait.tsx): a
  // forcePortrait modal is rotated 90° on mobile-landscape. Inside that rotated
  // box the vertical space is the device WIDTH (100vw), not its height — so the
  // card's `100vh` height cap resolves to the short landscape side and clips the
  // content. When locked, budget the cap against 100vw (the rotated box height).
  const supportsHover = useSupportsHover();
  const orientation = useOrientation();
  const portraitLocked = forcePortrait && !supportsHover && orientation === 'landscape';
  const cardMaxHeight = safeAreaMaxHeight(portraitLocked ? '100vw - 3rem' : '100vh - 3rem');
  const backdropRef = useRef<HTMLDivElement>(null);
  const borderRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Inline (grid) modals are scoped to their table cell. But a modal can be
  // mounted deep in a SHORT positioned ancestor — e.g. the leave-waitlist
  // confirm lives in JoinButton, inside the 64px action footer — so an absolute
  // inset:0 backdrop filled only that footer and showed as a clipped strip at
  // the bottom. Portal the inline modal to its cell root ([data-table-view-root]
  // on the GameInner grid cell) so it always fills the whole cell, wherever it's
  // mounted. An off-screen anchor lets us find THIS modal's own cell.
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [cellRoot, setCellRoot] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (isInline && isOpen) {
      setCellRoot((anchorRef.current?.closest('[data-table-view-root]') as HTMLElement) ?? null);
    } else {
      setCellRoot(null);
    }
  }, [isInline, isOpen]);

  useEffect(() => {
    if (!backdropRef.current || !contentRef.current || !borderRef.current) return;

    if (!isOpen) return;

    // EN-11003: reduced animations open the modal in place. Read through a ref
    // so a preference refresh doesn't replay the entrance.
    if (reduceAnimationsRef.current) {
      gsap.set(backdropRef.current, { opacity: 1 });
      gsap.set(contentRef.current, { y: 0, opacity: 1, scale: 1 });
      return;
    }

    gsap.to(backdropRef.current, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(
      contentRef.current,
      { y: 30, opacity: 0, scale: 0.97 },
      { y: 0, opacity: 1, scale: 1, duration: 0.35, ease: 'power3.out' }
    );

    // Animated border rotation, killed on close/unmount and when animations
    // are reduced (below) so an infinite tween never outlives the modal.
    borderTweenRef.current = gsap.to(borderRef.current, {
      '--border-angle': '360deg',
      duration: 4,
      repeat: -1,
      ease: 'none',
      modifiers: {
        '--border-angle': (val: string) => val
      }
    });
    return () => {
      borderTweenRef.current?.kill();
      borderTweenRef.current = null;
    };
    // cellRoot is a dep because an inline (grid) modal first renders with
    // cellRoot=null (mounted in place) and then re-renders once it resolves,
    // PORTALING the modal into the cell node — a swap that replaces the
    // backdrop/content/border DOM nodes. Without re-running here the entrance
    // tween animates the discarded initial nodes, leaving the final portaled
    // backdrop stuck at its start opacity:0 — an invisible layer that swallows
    // every click in the cell while showing no dialog.
  }, [isOpen, cellRoot]);

  useEffect(() => {
    if (!reduceAnimations) return;
    borderTweenRef.current?.kill();
    borderTweenRef.current = null;
  }, [reduceAnimations]);

  // Dialog consumers (ariaLabelledBy) land focus in the card on open and hand it
  // back on close. Re-run on the cellRoot swap, which replaces the card node (see
  // above). A grid shows several live tables, so there focus is never pulled out
  // of a field the player is typing in elsewhere (a raise amount, chat). Outside
  // a grid the card covers that field, so it takes focus regardless.
  //
  // Only while the player can see the card, since a card nobody can see can
  // still be focused, and the player's keys (Escape, Space) would then go to it:
  //   - its table is on screen: MultiTableGrid stays mounted behind the
  //     single-table view, so a hidden cell's card must wait for its view;
  //   - in MultiTableGrid, its cell is in the grid's scrolled row. The grid's
  //     on-screen flag is true for EVERY cell, including those scrolled past
  //     the row's edge (see contexts/TableOnScreenContext), so the row's own
  //     visibility channel (gridScrollChannel) decides. Any visible fraction
  //     counts, the same measure the header's fan-tile dots use.
  // It takes focus as soon as both hold: when its view is shown, or when its
  // cell scrolls into the row. When either stops holding while focus is in the
  // card, the card gives focus back to what had it, the same hand-back as a
  // close, and takes it again when it is seen again. If what had it is hidden
  // too (isFocusTargetSeen), focus is dropped instead and the target is kept
  // for the close.
  const isOnScreen = useIsTableOnScreen();
  // Re-runs the focus effect when the cell enters or leaves the row. Only a
  // trigger: the effect reads the channel itself, so a value left over from an
  // earlier spell is never trusted.
  const [isInGridRow, setIsInGridRow] = useState(false);
  useEffect(() => {
    if (!isOpen || !ariaLabelledBy || !isOnScreen) return undefined;
    const cellId = gridCellIdOf(cardRef.current);
    if (cellId === null) return undefined;
    const unsubscribe = subscribeGridVisibility((fractions) =>
      setIsInGridRow(isCellInGridRow(fractions, cellId))
    );
    return () => {
      unsubscribe();
      setIsInGridRow(false);
    };
  }, [isOpen, ariaLabelledBy, isOnScreen, cellRoot]);

  const returnFocusRef = useRef<HTMLElement | null>(null);
  // Whether this card's grid is behind the single-table view, as of its last
  // look, for the close hand-back (which runs once the card has gone).
  const gridHiddenRef = useRef(false);
  useEffect(() => {
    if (!isOpen || !ariaLabelledBy) return;
    const card = cardRef.current;
    const cellId = gridCellIdOf(card);
    gridHiddenRef.current = cellId !== null && !isOnScreen;
    const isSeen = isOnScreen && (cellId === null || isCellInGridRow(getGridVisibility(), cellId));
    const active = document.activeElement;
    if (!isSeen) {
      if (card && active instanceof HTMLElement && card.contains(active)) {
        const target = returnFocusRef.current;
        const usable = target?.isConnected && !card.contains(target);
        if (target && usable && isFocusTargetSeen(target, cellId !== null && !isOnScreen)) {
          returnFocusRef.current = null;
          target.focus({ preventScroll: true });
        } else {
          // A target hidden with the card (the control that opened it, in the
          // same off-row cell) is kept, so a later close still hands it back.
          if (!usable) returnFocusRef.current = null;
          active.blur();
        }
      }
      return;
    }
    if (!card || (active && card.contains(active))) return;
    // Never take focus from a field the player is typing in, as long as they
    // can see it: an input left in a cell scrolled out of the row is not in use.
    if (isInline && isEditableField(active) && isFocusTargetSeen(active as HTMLElement, false)) {
      return;
    }
    // In a grid, never take focus from another visible table's dialog the
    // player is using (two results landing close together): this card stays
    // reachable. A dialog in a hidden view is not in use, so it can lose focus.
    const activeDialog = active?.closest<HTMLElement>('[role="dialog"]');
    if (
      isInline &&
      activeDialog &&
      gridCellIdOf(activeDialog) !== null &&
      isFocusTargetSeen(activeDialog, false)
    ) {
      return;
    }
    if (!returnFocusRef.current && active instanceof HTMLElement && active !== document.body) {
      returnFocusRef.current = active;
    }
    card.focus({ preventScroll: true });
  }, [isOpen, ariaLabelledBy, cellRoot, isInline, isOnScreen, isInGridRow]);

  useEffect(() => {
    if (!ariaLabelledBy) return undefined;
    const restoreFocus = () => {
      const target = returnFocusRef.current;
      returnFocusRef.current = null;
      const active = document.activeElement;
      // Only when focus left with the card: never yank it from where the player
      // has since moved it. Never onto a control the player cannot see (one
      // kept while its cell was scrolled out of the row, or anywhere in the
      // grid while the single-table view covers it).
      if (
        target?.isConnected &&
        (!active || active === document.body) &&
        isFocusTargetSeen(target, gridHiddenRef.current)
      ) {
        target.focus({ preventScroll: true });
      }
    };
    if (!isOpen) restoreFocus();
    return isOpen ? restoreFocus : undefined;
  }, [isOpen, ariaLabelledBy]);

  // Escape closes like the X. Outside a grid the dialog is modal, so Tab cycles
  // inside the card; in a grid the other tables stay live and reachable.
  const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (closeOnEscape && onClose) {
        event.stopPropagation();
        onClose();
      }
      return;
    }
    if (event.key !== 'Tab' || isInline || !cardRef.current) return;
    const focusable = Array.from(
      cardRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter((el) => !el.hasAttribute('disabled'));
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === cardRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  // Use a different approach for the border animation — CSS animation via a style tag
  useEffect(() => {
    if (!isOpen) return;

    const el = borderRef.current;
    if (!el) return;

    let frame: number;
    let angle = 0;

    const paint = () => {
      el.style.background = `conic-gradient(from ${angle}deg, transparent 0%, #E98C26 10%, transparent 20%, transparent 50%, rgba(98, 139, 72, 0.6) 60%, transparent 70%)`;
    };
    // EN-11003: reduced animations paint the border once and keep it still.
    if (reduceAnimations) {
      paint();
      return;
    }

    const animate = () => {
      angle = (angle + 0.8) % 360;
      paint();
      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
    // See the entrance effect above: re-bind to the portaled border node after
    // the cellRoot swap so the glow animates the node that's actually on screen.
  }, [isOpen, cellRoot, reduceAnimations]);

  // A backdrop dismiss has to belong to the backdrop for its WHOLE gesture.
  // Listening for `click` alone is not enough: a click is delivered to the
  // nearest common ancestor of pointer-down and pointer-up, so dragging the
  // buy-in slider inside the card and releasing past its edge produces a click
  // whose target IS the backdrop — the old "slams shut mid-adjust" bug, which
  // was previously worked around by disabling backdrop dismissal altogether.
  // Requiring pointer-down AND pointer-up to land on the backdrop itself keeps
  // drags and text selections that begin inside the card from closing it, in
  // both directions.
  //
  // We settle on `pointerup` rather than `click` because it reports mouse,
  // touch and pen identically (iOS/Android run this same client in a Capacitor
  // WebView), and because the table-swipe gesture layer can suppress the
  // synthetic click on touch while leaving pointer events intact.
  //
  // The backdrop also SCROLLS (tall modals like the KYC questionnaire overflow
  // it), so a drag that starts and ends on the backdrop is a scroll, not a
  // dismiss. That is caught by movement distance rather than `pointercancel` —
  // see utils/tapGesture for the measurements behind that choice.
  //
  // The gesture belongs to ONE pointer from down to up. Remembering only an
  // origin would let any later release be measured against it, so a second
  // finger touching the backdrop (pinch-zooming an overflowing modal, a thumb
  // resting on the edge) could close the modal on its own release without
  // anyone having completed an outside tap. Keep the pointerId with the origin,
  // and treat a non-primary pointer joining in as the end of the tap.
  const scrimGestureRef = useRef<{ pointerId: number; origin: TapOrigin } | null>(null);

  const isScrimItself = (event: React.SyntheticEvent<HTMLDivElement>) =>
    event.target === event.currentTarget;

  const handleScrimPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A second finger makes this a multi-touch gesture, not a tap: abandon the
    // one in progress rather than letting either finger's release dismiss.
    if (event.isPrimary === false) {
      scrimGestureRef.current = null;
      return;
    }

    scrimGestureRef.current = isScrimItself(event)
      ? { pointerId: event.pointerId, origin: tapOrigin(event) }
      : null;
  };

  const handleScrimPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const gesture = scrimGestureRef.current;
    // Only the pointer that began the gesture can complete it; another
    // pointer's release leaves it pending.
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    scrimGestureRef.current = null;

    if (!closeOnBackdropClick || !onClose) return;
    if (!isScrimItself(event)) return;
    // Secondary / middle buttons open menus, they are not a dismiss gesture.
    if (event.button > 0) return;
    // Moved too far: a scroll or a selection, not a tap.
    if (!isWithinTapSlop(gesture.origin, event)) return;

    // Closing usually unmounts this modal synchronously, and on touch the
    // engine then hit-tests the synthetic click against whatever the scrim was
    // covering — measured firing FOLD straight through a dismissed
    // fold-confirm in both Chromium and WebKit. Consume that click first.
    swallowNextClickNear(gesture.origin);

    onClose();
  };

  if (!isOpen) return null;

  const modal = (
    <div
      ref={backdropRef}
      data-testid='game-modal-backdrop'
      style={isInline ? inlineBackdropStyle : backdropStyle}
      onPointerDown={handleScrimPointerDown}
      onPointerUp={handleScrimPointerUp}>
      <div
        ref={contentRef}
        style={{ ...outerWrapStyle, maxWidth }}
        onClick={(e) => e.stopPropagation()}>
        {/* Animated border layer */}
        <div ref={borderRef} style={borderLayerStyle} />

        {/* Card content — sits inside the border with a small gap */}
        <div
          ref={cardRef}
          style={{
            ...cardStyle,
            maxHeight: cardMaxHeight,
            ...(ariaLabelledBy ? { outline: 'none' } : null)
          }}
          {...(ariaLabelledBy
            ? {
                role: 'dialog',
                // A grid cell's dialog must not mark the other live tables inert.
                'aria-modal': !isInline,
                'aria-labelledby': ariaLabelledBy,
                tabIndex: -1,
                onKeyDown: handleDialogKeyDown
              }
            : null)}>
          {/* Header — only when there's a title; the close X is rendered as a
              floating absolute corner button below so the body stays vertically
              centered when no title is present. */}
          {title && (
            <div style={headerStyle}>
              <span style={titleAlign === 'center' ? centeredTitleStyle : titleStyle}>{title}</span>
              {/* Left-aligned titles keep the spacer that pins them to the
                  left edge; a centred title takes the whole row instead. */}
              {titleAlign === 'center' ? null : <div style={{ flex: 1 }} />}
            </div>
          )}

          {/* Floating close X — pinned to the top-right corner so it doesn't
              steal vertical space from the body. */}
          {showClose && onClose && (
            <button onClick={onClose} style={floatingCloseBtnStyle} aria-label='Close'>
              <CloseIcon style={{ fontSize: '1.1rem' }} />
            </button>
          )}

          {/* Body — the shared scroll region behind every modal built on this
              shell, tagged once here rather than in each of them. Owns its own
              scroll: the table-swipe Observer must hand wheel gestures back to
              the browser instead of preventDefault-ing them, or a tall modal
              stops scrolling as soon as a second table arms the gesture. See
              utils/swipeIgnore. */}
          <div style={bodyStyle} {...swipeIgnoreProps}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // The fixed-overlay variant must escape any transformed/clipping ancestor
  // (e.g. TopAction's centerSection uses `transform: translate(-50%, -50%)`,
  // which makes `position: fixed` resolve against that tiny slot instead of the
  // viewport — the exit-waitlist confirm modal was sized/centered to it and
  // broke in mobile landscape). Portal to <body>, the same escape hatch
  // JoinActions uses.
  //
  // Lobby-launched modals additionally opt into ForcePortrait so they rotate
  // WITH the portrait-locked lobby on mobile-landscape (the wrapper has to live
  // inside the portal, since the portal escapes the lobby's own wrapper — same
  // pattern as FilterDrawer / PromoDetailsModal). Non-opted modals pass through
  // untouched (ForcePortrait collapses to display:contents when not locking).
  if (!isInline) {
    return ReactDOM.createPortal(
      forcePortrait ? <ForcePortrait>{modal}</ForcePortrait> : modal,
      document.body
    );
  }

  // Inline/grid mode: portal to THIS modal's table cell root so it fills the
  // whole cell (not a short footer/ancestor it happens to be mounted in). The
  // anchor stays in place so `closest` can locate the cell; fall back to
  // rendering in-place if no cell root is found (e.g. non-grid inline usage).
  return (
    <>
      <span ref={anchorRef} aria-hidden style={{ display: 'none' }} />
      {cellRoot ? ReactDOM.createPortal(modal, cellRoot) : modal}
    </>
  );
};

export default GameModal;

/* ── Shared button styles for use inside modals ── */

export const modalActionBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.4rem',
  padding: '0.75rem 1.5rem',
  border: 'none',
  borderRadius: '10px',
  fontSize: '0.9rem',
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  minHeight: '48px',
  flex: 1,
  // `flex: 1` alone does NOT let these share a narrow row. A flex item's default
  // `min-width: auto` floors it at min-content — here the label plus 48px of
  // horizontal padding — so two long labels simply overrun the row instead of
  // shrinking, and the card's `overflow: hidden` clips the second one.
  //
  // Measured at a 375px viewport: the Unregister + Tournament Info row in
  // TournamentDetailModal wants 260px of a 244px row and loses 16px off the
  // right edge of the button. It fit before EN-10789 widened the scrim gutters
  // to a reachable 44px (card 340px -> 284px), which is what took the slack out.
  // Buying the width back by narrowing the gutters again would undo the ticket,
  // so let the buttons do what `flex: 1` already says they should: shrink, and
  // wrap their label onto a second line (they are `minHeight`, not fixed
  // height, so the row grows instead of clipping).
  minWidth: 0
};

export const primaryBtnStyle: CSSProperties = {
  ...modalActionBtnStyle,
  background: 'linear-gradient(135deg, #E98C26 0%, #d47a1a 100%)',
  color: '#ffffff',
  boxShadow: '0 2px 8px rgba(233, 140, 38, 0.3)'
};

export const secondaryBtnStyle: CSSProperties = {
  ...modalActionBtnStyle,
  background: 'rgba(255, 255, 255, 0.08)',
  color: 'rgba(255, 255, 255, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.1)'
};

export const dangerBtnStyle: CSSProperties = {
  ...modalActionBtnStyle,
  background: 'rgba(200, 60, 60, 0.2)',
  color: '#e05555',
  border: '1px solid rgba(200, 60, 60, 0.3)'
};

export const btnRowStyle: CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  width: '100%',
  marginTop: '0.5rem'
};

export const labelStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.45)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

export const valueStyle: CSSProperties = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: 'rgba(255, 255, 255, 0.9)'
};

export const infoRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.5rem 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)'
};

/* ── Internal styles ── */

/**
 * Width of the scrim strip beside the card — the part of the backdrop a thumb
 * can actually reach to dismiss. 44px is the standard minimum touch target.
 *
 * The inline (grid) backdrop keeps its own small padding: a grid cell is a
 * fraction of the viewport, so a 44px gutter there would leave almost no card.
 */
const MODAL_SIDE_GUTTER = '2.75rem';

const backdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  // alignItems:flex-start (not center) + margin:auto on the inner wrap is the
  // safe-center pattern: card stays centered when it fits, but when content
  // exceeds the viewport (e.g. KYC questions on mobile) the top stays
  // reachable and the backdrop's overflow-y:auto can scroll the rest into
  // view. With alignItems:center, the top overflow is clipped above the
  // scroll origin and cannot be reached.
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 200,
  // Pad by the device safe-area insets (plus the base 1rem) so the centered
  // card never sits under the notch or home indicator on mobile.
  ...safeAreaModalBackdropPadding(),
  // Side gutters are WIDER than that base, because on a phone this strip is
  // the whole of the dismiss target beside a full-width card. At 1rem it
  // measured 17-19px — well under the ~44px minimum touch target — so
  // tap-outside was technically working and practically unhittable at the
  // sides. Vertical padding is deliberately left alone: a card that fits is
  // margin:auto-centred with hundreds of px above and below it, and a card
  // that overflows needs its top reachable, so growing it there would only
  // eat into the scroll area.
  paddingLeft: `calc(${MODAL_SIDE_GUTTER} + ${SAFE_AREA_LEFT})`,
  paddingRight: `calc(${MODAL_SIDE_GUTTER} + ${SAFE_AREA_RIGHT})`,
  opacity: 0,
  // Keep overflow scrolling so tall content (e.g. KYC questions on mobile)
  // stays reachable — see the safe-center note above.
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch'
};

const inlineBackdropStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  zIndex: 200,
  padding: '0.5rem',
  opacity: 0,
  overflow: 'auto',
  WebkitOverflowScrolling: 'touch'
};

const outerWrapStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  borderRadius: '17px',
  padding: '1.5px',
  // Pairs with backdrop alignItems:flex-start: centers vertically when there's
  // headroom, otherwise pins to the top so the user can scroll down to see
  // overflow content. Don't shrink — keep natural height so scrolling works.
  margin: 'auto',
  flexShrink: 0
};

const borderLayerStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '17px',
  background: 'conic-gradient(from 0deg, transparent 0%, #E98C26 10%, transparent 20%)',
  opacity: 0.7
};

const cardStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  // Height cap is applied inline (see cardMaxHeight): it budgets against 100vh
  // normally, but 100vw when the modal is portrait-locked on mobile-landscape
  // (the rotated box's vertical space is the device width). Without a cap, tall
  // modals like TournamentDetailModal clip off the bottom and the action buttons
  // drop below the fold with no way to reach them.
  // Column layout so the header is fixed at top, the body grows + scrolls.
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'rgba(20, 30, 42, 0.98)',
  backdropFilter: 'blur(16px)',
  borderRadius: '16px',
  boxShadow: '0 16px 48px rgba(0, 0, 0, 0.6)',
  overflow: 'hidden',
  fontFamily: "'Outfit', sans-serif",
  color: 'rgba(255, 255, 255, 0.9)'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '1rem 1.25rem 0.5rem',
  gap: '0.5rem',
  // Sticks at the top of the flex card; body below it does the scrolling.
  flexShrink: 0
};

const titleStyle: CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#ffffff'
};

// titleAlign='center': the title takes the whole header row and centres inside
// it, so it lines up with the body copy and action button beneath it.
const centeredTitleStyle: CSSProperties = {
  ...titleStyle,
  flex: 1,
  textAlign: 'center'
};

const floatingCloseBtnStyle: CSSProperties = {
  position: 'absolute',
  top: '0.6rem',
  right: '0.6rem',
  width: '32px',
  height: '32px',
  background: 'rgba(255, 255, 255, 0.06)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '50%',
  color: 'rgba(255, 255, 255, 0.6)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.15s ease',
  zIndex: 2,
  padding: 0
};

const bodyStyle: CSSProperties = {
  padding: '0.75rem 1.25rem 1.25rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  // Grow to fill the card vertically, then scroll if the children exceed
  // the available height. minHeight: 0 is required for the overflow to
  // engage inside a flex column — without it, flex children compute
  // intrinsic min-content height and the scrollbar never appears.
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  // Hint to mobile / touchpad browsers to scroll smoothly inside the
  // modal rather than chaining to the document.
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch'
};
